import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { clearErrorAction, usersRequestByAdmin, deleteUserByAdminAction } from '../redux/actions/userAction'
import swal from 'sweetalert';
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import Loader from '../layout/loader'
import Sidebar from './Sidebar'

import './allUserByAdmin.css'

import { DELETE_USER_DETAIL_RESET } from '../redux/constants/userConstants'

function AllUserByAdmin() {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');

    const { loading, error, users } = useSelector((state) => state.allUsers)
    const { loading: deletedLoading, error: deleteError, isDeleted } = useSelector((state) => state.profileUpdateByUser)

    const userList = useMemo(() => Array.isArray(users) ? users : [], [users]);

    const getUserName = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Customer';

    const getRoleTone = (role) => String(role || '').toLowerCase() === 'admin' ? 'admin' : 'customer';

    const deleteUserHandler = (id, name) => {
        swal({
            title: "Delete user?",
            text: `This will remove ${name || 'this user'} from the website.`,
            icon: "warning",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                dispatch(deleteUserByAdminAction(id))
            }
        })
    }

    useEffect(() => {
        if (error) {
            swal({
                title: "Validation",
                text: error,
                icon: "error",
            })
            dispatch(clearErrorAction())

        }

        if (deleteError) {
            swal({
                title: "Validation",
                text: deleteError,
                icon: "error",
            })
            dispatch(clearErrorAction())

        }
        if (isDeleted) {
            swal({
                title: "Deleted.",
                text: isDeleted,
                icon: "success",
            })
            dispatch({ type: DELETE_USER_DETAIL_RESET })
        }

        dispatch(usersRequestByAdmin())

    }, [deleteError, dispatch, error, isDeleted])

    const roleOptions = useMemo(() => {
        const roles = new Set(userList.map((user) => user.role).filter(Boolean));
        return ['All', ...Array.from(roles)];
    }, [userList]);

    const filteredUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return userList.filter((user) => {
            const roleMatch = roleFilter === 'All' || user.role === roleFilter;
            const searchableText = [
                user._id,
                getUserName(user),
                user.email,
                user.number,
                user.gender,
                user.role,
            ].join(' ').toLowerCase();

            return roleMatch && (!normalizedSearch || searchableText.includes(normalizedSearch));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userList, searchTerm, roleFilter]);

    const totalUsers = userList.length;
    const adminUsers = userList.filter((item) => item.role === 'admin').length;
    const customerUsers = totalUsers - adminUsers;
    const recentUsers = userList.filter((item) => {
        const createdDate = item.createdAt || item.date;
        if (!createdDate) {
            return false;
        }

        const daysSinceCreated = (Date.now() - new Date(createdDate).getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceCreated <= 30;
    }).length;

    return (
        <Fragment>
            {deletedLoading ? <Loader /> : (<Fragment>
                {loading ? <Loader /> : (
                    <Fragment>
                        <div className="dashboard-container">
                            <Sidebar />
                            <div className="productListContainer admin-users-page">
                                <div className='admin-list-hero admin-users-hero'>
                                    <div>
                                        <span className="admin-hero-kicker">Account operations</span>
                                        <h1>All users</h1>
                                        <p>Manage customer accounts, admin roles, access, and contact details.</p>
                                    </div>
                                    <div className="admin-users-hero-stats">
                                        <div>
                                            <span>Total users</span>
                                            <strong>{totalUsers}</strong>
                                        </div>
                                        <div>
                                            <span>New in 30 days</span>
                                            <strong>{recentUsers}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className='admin-users-toolbar'>
                                    <input
                                        type='search'
                                        placeholder='Search name, email, mobile, user ID...'
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                                        {roleOptions.map((role) => (
                                            <option value={role} key={role}>{role}</option>
                                        ))}
                                    </select>
                                    <div className='admin-users-summary-card'>
                                        <span>Admins</span>
                                        <strong>{adminUsers}</strong>
                                    </div>
                                    <div className='admin-users-summary-card'>
                                        <span>Customers</span>
                                        <strong>{customerUsers}</strong>
                                    </div>
                                </div>

                                <div className="admin-users-table-card">
                                    <div className="admin-users-table-scroll">
                                        <table className="admin-users-table">
                                            <thead>
                                                <tr>
                                                    <th>User</th>
                                                    <th>Contact</th>
                                                    <th>Profile</th>
                                                    <th>Role</th>
                                                    <th>Joined</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.length > 0 ? filteredUsers.map((user) => {
                                                    const joinedDate = user.createdAt || user.date;
                                                    const userName = getUserName(user);

                                                    return (
                                                        <tr key={user._id}>
                                                            <td data-label="User">
                                                                <div className="admin-user-identity">
                                                                    <div className="admin-user-avatar">
                                                                        {userName.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <strong>{userName}</strong>
                                                                        <span>{user._id}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td data-label="Contact">
                                                                <div className="admin-user-contact">{user.email || 'No email'}</div>
                                                                <span>{user.number || 'No mobile number'}</span>
                                                            </td>
                                                            <td data-label="Profile">
                                                                <div className="admin-user-profile">{user.gender || 'not specified'}</div>
                                                                <span>{user.fullName || userName}</span>
                                                            </td>
                                                            <td data-label="Role">
                                                                <span className={`admin-user-chip role-${getRoleTone(user.role)}`}>
                                                                    {user.role || 'user'}
                                                                </span>
                                                            </td>
                                                            <td data-label="Joined">
                                                                <strong className="admin-user-date">
                                                                    {joinedDate ? new Date(joinedDate).toLocaleDateString('en-IN', {
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        year: 'numeric'
                                                                    }) : 'No date'}
                                                                </strong>
                                                            </td>
                                                            <td data-label="Action">
                                                                <div className="admin-user-actions">
                                                                    <Link className="admin-user-edit-button" to={`/Admin/dashboard/Users/${user._id}`}>
                                                                        <EditIcon /> Edit
                                                                    </Link>
                                                                    <button className="admin-user-delete-button" onClick={() => deleteUserHandler(user._id, userName)}>
                                                                        <DeleteIcon /> Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                }) : (
                                                    <tr>
                                                        <td colSpan="6">
                                                            <div className="admin-users-empty">
                                                                <strong>No users found</strong>
                                                                <span>Try changing the search or role filter.</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </Fragment>
                )}

            </Fragment>)}
        </Fragment>
    )
}

export default AllUserByAdmin
